import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Plus, Loader2 } from "lucide-react";
import client, { formatApiError } from "@/lib/api";
import { useMasters } from "@/lib/useMasters";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

function MasterSelect({ label, type, options, value, onChange, testid, canAdd, onAdded, isThickness }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [thk, setThk] = useState("");
  const [busy, setBusy] = useState(false);

  const add = async () => {
    setBusy(true);
    try {
      const body = isThickness
        ? { displayName: `${thk} mm`, thicknessMm: parseFloat(thk) }
        : { displayName: name.trim() };
      const { data } = await client.post(`/master-data/${type}`, body);
      toast.success(`${label} added`);
      await onAdded(data.data.id);
      setAdding(false);
      setName("");
      setThk("");
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.existingId) {
        toast.message("Already exists", { description: "Selecting the existing value." });
        await onAdded(d.existingId);
        setAdding(false);
      } else {
        toast.error(formatApiError(d));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Label className="text-xs font-medium text-slate-600 mb-1 flex items-center justify-between">
        {label}
        {canAdd && (
          <Popover open={adding} onOpenChange={setAdding}>
            <PopoverTrigger asChild>
              <button type="button" className="text-violet-700 text-xs font-semibold inline-flex items-center gap-0.5 hover:underline"
                data-testid={`add-${testid}-trigger`}>
                <Plus size={13} /> Add new
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-64" align="end">
              <p className="text-sm font-medium mb-2">New {label}</p>
              {isThickness ? (
                <Input type="number" step="0.1" placeholder="Thickness (mm)" value={thk}
                  onChange={(e) => setThk(e.target.value)} data-testid={`add-${testid}-input`} />
              ) : (
                <Input placeholder={`${label} name`} value={name} onChange={(e) => setName(e.target.value)}
                  data-testid={`add-${testid}-input`} />
              )}
              <Button size="sm" className="w-full mt-2 text-white" style={{ background: "#0D9488" }}
                onClick={add} disabled={busy || (isThickness ? !thk : !name.trim())} data-testid={`add-${testid}-save`}>
                {busy ? <Loader2 className="animate-spin" size={15} /> : "Add"}
              </Button>
            </PopoverContent>
          </Popover>
        )}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={`capture-${testid}`}><SelectValue placeholder={`Select ${label.toLowerCase()}`} /></SelectTrigger>
        <SelectContent className="max-h-64">
          {(options || []).map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.display_name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export default function CaptureModal({ open, onClose }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data: masters, refetch } = useMasters();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    jobReference: "",
    customerId: "",
    programmerId: "",
    materialGradeId: "",
    materialOwnershipId: "",
    thicknessId: "",
    machineId: "",
    weightKg: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const addedTo = async (key, id) => {
    await refetch();
    set(key, id);
  };

  const submit = async () => {
    for (const [k, label] of [
      ["jobReference", "Job reference"], ["customerId", "Customer"], ["programmerId", "Programmer"],
      ["materialGradeId", "Material grade"], ["materialOwnershipId", "Material ownership"],
      ["thicknessId", "Thickness"], ["machineId", "Machine"],
    ]) {
      if (!form[k]) return toast.error(`${label} is required`);
    }
    if (form.weightKg === "" || isNaN(parseFloat(form.weightKg)) || parseFloat(form.weightKg) < 0) return toast.error("Processed weight cannot be negative");
    setBusy(true);
    try {
      await client.post("/nesting-records", {
        date: form.date,
        jobReference: form.jobReference,
        customerId: form.customerId,
        programmerId: form.programmerId,
        materialGradeId: form.materialGradeId,
        materialOwnershipId: form.materialOwnershipId,
        thicknessId: form.thicknessId,
        machineId: form.machineId,
        weightKg: parseFloat(form.weightKg),
        clientRequestId: crypto.randomUUID(),
      });
      toast.success("Nesting record captured");
      qc.invalidateQueries();
      onClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const canManage = can("master.manage");

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="capture-modal">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">New Nesting Record</DialogTitle>
          <DialogDescription className="sr-only">Capture a new laser nesting record with controlled master-data fields.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 mt-2">
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1 block">Nesting date</Label>
            <Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="capture-date" />
          </div>
          <div>
            <Label className="text-xs font-medium text-slate-600 mb-1 block">PO / Job reference</Label>
            <Input value={form.jobReference} placeholder="PO-10482" onChange={(e) => set("jobReference", e.target.value)} data-testid="capture-jobref" />
          </div>
          <MasterSelect label="Customer" type="customers" options={masters?.customers} value={form.customerId}
            onChange={(v) => set("customerId", v)} testid="customer" canAdd={canManage} onAdded={(id) => addedTo("customerId", id)} />
          <MasterSelect label="Programmed by" type="programmers" options={masters?.programmers} value={form.programmerId}
            onChange={(v) => set("programmerId", v)} testid="programmer" canAdd={canManage} onAdded={(id) => addedTo("programmerId", id)} />
          <MasterSelect label="Material grade" type="grades" options={masters?.grades} value={form.materialGradeId}
            onChange={(v) => set("materialGradeId", v)} testid="grade" canAdd={canManage} onAdded={(id) => addedTo("materialGradeId", id)} />
          <MasterSelect label="Material ownership" type="ownership" options={masters?.ownership} value={form.materialOwnershipId}
            onChange={(v) => set("materialOwnershipId", v)} testid="ownership" canAdd={canManage} onAdded={(id) => addedTo("materialOwnershipId", id)} />
          <MasterSelect label="Thickness" type="thicknesses" options={masters?.thicknesses} value={form.thicknessId}
            onChange={(v) => set("thicknessId", v)} testid="thickness" canAdd={canManage} onAdded={(id) => addedTo("thicknessId", id)} isThickness />
          <MasterSelect label="Machine" type="machines" options={masters?.machines} value={form.machineId}
            onChange={(v) => set("machineId", v)} testid="machine" canAdd={false} onAdded={() => {}} />
          <div className="sm:col-span-2">
            <Label className="text-xs font-medium text-slate-600 mb-1 block">Processed weight (kg)</Label>
            <Input type="number" step="0.001" placeholder="1842.75" value={form.weightKg}
              onChange={(e) => set("weightKg", e.target.value)} data-testid="capture-weight" />
          </div>
        </div>
        <div className="flex gap-2 mt-4">
          <Button variant="outline" className="flex-1" onClick={onClose} data-testid="capture-cancel">Cancel</Button>
          <Button className="flex-1 text-white font-semibold" style={{ background: "#0D9488" }} onClick={submit} disabled={busy} data-testid="capture-submit">
            {busy ? <Loader2 className="animate-spin" size={16} /> : "Save Record"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
